import { OnDestroy, Pipe, PipeTransform } from '@angular/core';
import { GlobalService } from 'app/global/global.service';
import { ICompanySettingDTO } from 'app/main/pages/settings/company/company-settings.dto';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Pipe({ name: "numberFormatPipe", pure: false })
export class NumberFormatPipe implements PipeTransform, OnDestroy  {
  private companySettings$: ICompanySettingDTO | null = null;
  private destroy$ = new Subject<void>();

  constructor(private _globalService: GlobalService) {
    this._globalService.getUserCompanySettings().pipe(
      takeUntil(this.destroy$)
    ).subscribe(settings => {
      this.companySettings$ = settings as ICompanySettingDTO;
    });
  }


  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  transform(value: number, shouldConcatCurrency: boolean = false, defaultPointValue: boolean = true, overrideSettings?: ICompanySettingDTO): string {
    if (value == null || (!this.companySettings$ && !overrideSettings)) return '';
    else if (value == 0) return '0';

    const settings = overrideSettings || this.companySettings$;

    // Get locale and specific formatting options from the format type
    const locale = settings?.pricing_format_amount || 'en-US';
    const options: Intl.NumberFormatOptions = this.getFormatOptions(settings?.pricing_format_example, value, defaultPointValue);

    // Format the price
    const formattedPrice = new Intl.NumberFormat(locale, options).format(value);

    // Append or prepend currency based on configuration
    return settings?.pricing_format_currency === "after_value"
      ? `${formattedPrice}${shouldConcatCurrency ? ' ' + settings?.system_currency : ''}`
      : `${shouldConcatCurrency ? settings?.system_currency + ' ' : ''}${formattedPrice}`;
  }

  private getFormatOptions(formatType: string, value: number, defaultPointValue: boolean): Intl.NumberFormatOptions {
    let useGrouping = true;
    let minimumFractionDigits = 0;
    let maximumFractionDigits = 0;

    // Determine the decimal separator and grouping based on the format type
    let decimalSymbol = '.';
    if (formatType.includes(',')) decimalSymbol = ',';  // If format type has a comma, use ',' as decimal
    if (formatType.includes(' ')) useGrouping = true;  // If format type includes space, use grouping

    // If format type is '1000.50', turn off grouping
    if (formatType === '1000.50') useGrouping = false;

    // If the value has decimals or the defaultPointValue flag is true, use 2 decimal places
    if (value % 1 !== 0 || defaultPointValue) {
      minimumFractionDigits = 2;
      maximumFractionDigits = 2;
    } else {
      // If it's a whole number, set to no decimals
      minimumFractionDigits = 0;
      maximumFractionDigits = 0;
    }

    // Return the formatting options, including handling the decimalSymbol condition
    return {
      minimumFractionDigits,
      maximumFractionDigits,
      useGrouping,
      ...(decimalSymbol === ',' && { style: 'decimal' })
    };
  }
}
